import {
  useInfiniteQuery,
  UseInfiniteQueryOptions,
  UseInfiniteQueryResult,
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import {
  defaultGetNextPageParam,
  sortObjectKeys,
} from '@vindr/utils/helpers/common.helper';
import uniqBy from 'lodash/uniqBy';
import {
  createLayout,
  deleteLayout,
  getLayouts,
  ParamsCreateLayout,
  ParamsUpdateLayout,
  updateLayout,
} from '../apis/layout.api';
import { LAYOUT_TYPE } from '../constants/common.constant';
import { queryKey } from '../constants/queryClientKey';

export function useGetLayouts(
  params: Parameters<typeof getLayouts>[0],
  options?: UseQueryOptions
) {
  return useQuery(
    [queryKey.layout_report, sortObjectKeys(params)],
    () => getLayouts(params),
    {
      ...options,
    }
  ) as UseQueryResult<Awaited<ReturnType<typeof getLayouts>>>;
}

export function useGetLayoutByStatus(
  {
    institution_ids,
    name,
    status,
    user_ids,
    ...props
  }: Parameters<typeof getLayouts>[0],
  options?: UseQueryOptions
) {
  const isPrivate = status === LAYOUT_TYPE.PRIVATE;
  const generalParams = {
    limit: 100,
    offset: 0,
    active: true,
    institution_ids,
    ...(name && { name }),
    ...props,
  } satisfies Parameters<typeof getLayouts>[0];

  // Fetch Public Layouts
  const {
    data: responsePublicLayouts,
    isInitialLoading: loadingPublic,
    isFetching: fetchingPublic,
  } = useQuery(
    [queryKey.layout_report, 'publicLayouts', institution_ids, name],
    () => getLayouts({ ...generalParams, status: LAYOUT_TYPE.PUBLIC }),
    {
      enabled: !!institution_ids?.length, // Only fetch if institutionId is provided
      ...options,
    }
  ) as UseQueryResult<Awaited<ReturnType<typeof getLayouts>>>;

  // Fetch Private Layouts
  const {
    data: responsePrivateLayouts,
    isInitialLoading: loadingPrivate,
    isFetching: fetchingPrivate,
  } = useQuery(
    [queryKey.layout_report, 'privateLayouts', institution_ids, name, user_ids],
    () =>
      getLayouts({ ...generalParams, status: LAYOUT_TYPE.PRIVATE, user_ids }),
    {
      enabled: isPrivate && !!institution_ids.length && !!user_ids.length, // Only fetch if all required conditions are met
      ...options,
    }
  ) as UseQueryResult<Awaited<ReturnType<typeof getLayouts>>>;

  // Combine public and private layouts and remove duplicates
  const publicLayouts = responsePublicLayouts?.data || [];
  const privateLayouts = responsePrivateLayouts?.data || [];

  const data = isPrivate
    ? uniqBy([...publicLayouts, ...privateLayouts], 'id')
    : publicLayouts;

  const isInitialLoading = loadingPublic || loadingPrivate;
  const isFetching = fetchingPublic || fetchingPrivate;

  return {
    data,
    isInitialLoading,
    isFetching,
  };
}

export function useInfinityGetLayouts(
  params: Parameters<typeof getLayouts>[0],
  options?: UseInfiniteQueryOptions
) {
  return useInfiniteQuery(
    [queryKey.account, sortObjectKeys(params)],
    (e) =>
      getLayouts({
        ...params,
        offset: (e.pageParam || 0) + (params?.offset || 0),
      }),
    {
      getNextPageParam: defaultGetNextPageParam,
      ...options,
    }
  ) as UseInfiniteQueryResult<Awaited<ReturnType<typeof getLayouts>>>;
}

export function useCreateLayout() {
  const queryClient = useQueryClient();

  return useMutation(
    async (params: ParamsCreateLayout) => createLayout(params),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([queryKey.layout_report]);
      },
      onError: (err) => {
        console.error('useCreateLayout:', err);
      },
    }
  );
}

export function useUpdateLayout() {
  const queryClient = useQueryClient();

  return useMutation(
    async ({ id, data }: { id: string; data: ParamsUpdateLayout }) =>
      updateLayout(id, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([queryKey.layout_report]);
      },

      onError: (err) => {
        console.error('useUpdateLayout:', err);
      },
    }
  );
}

export function useDeleteLayout() {
  const queryClient = useQueryClient();

  return useMutation(async (id: string) => deleteLayout(id), {
    onSuccess: () => {
      queryClient.invalidateQueries([queryKey.layout_report]);
    },
    onError: (err) => {
      console.error('useDeleteLayout:', err);
    },
  });
}
